import { FiUser, FiLogOut, FiGrid, FiPlus, FiSun, FiMoon } from 'react-icons/fi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/ui/dropdown-menu';
import { Button } from '@/components/atoms/ui/button';
import { cn } from '@/lib/utils';
import { CreateBoardDialog } from '@/components/organisms/CreateBoardDialog';
import { User } from '@/types/models';
import { UserAvatar } from '@/components/atoms/UserAvatar';
import { MenuItem } from '@/components/molecules/MenuItem';
import { useTheme } from '@/contexts/ThemeContext';

interface UserMenuComponentProps {
  user: User;
  photoURL: string | null;
  variant?: 'default' | 'board';
  hideCreateBoard?: boolean;
  createBoardDialogOpen: boolean;
  onCreateBoardDialogOpenChange: (open: boolean) => void;
  onMyProfileClick: () => void;
  onBrowseBoardsClick: () => void;
  onSignOut: () => void;
  onCreateBoardClick: () => void;
}

export const UserMenuComponent: React.FC<UserMenuComponentProps> = ({
  user,
  photoURL,
  variant = 'default',
  hideCreateBoard = false,
  createBoardDialogOpen,
  onCreateBoardDialogOpenChange,
  onMyProfileClick,
  onBrowseBoardsClick,
  onSignOut,
  onCreateBoardClick,
}) => {
  const isBoardVariant = variant === 'board';
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hover:opacity-80 p-0 text-gray-700 dark:text-[#929bc9]"
        >
          <UserAvatar photoURL={photoURL} username={user.username} size="sm" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(
          'user-menu-component',
          isBoardVariant
            ? 'bg-white/90 dark:bg-black/40 backdrop-blur-xl border-gray-200 dark:border-white/10 text-gray-900 dark:text-white'
            : 'bg-popover border-border text-popover-foreground'
        )}
      >
        <DropdownMenuLabel
          className={cn(isBoardVariant ? 'text-gray-900 dark:text-white' : 'text-popover-foreground')}
        >
          <div className="flex items-center gap-3">
            <UserAvatar photoURL={photoURL} username={user.username} size="md" />
            <div>
              <p className="font-medium">{user.username}</p>
              <p
                className={cn(
                  'text-xs truncate max-w-[150px]',
                  isBoardVariant ? 'text-gray-600 dark:text-white/70' : 'text-muted-foreground'
                )}
              >
                {user.email}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator
          className={cn(isBoardVariant ? 'bg-gray-200 dark:bg-white/10' : 'bg-border')}
        />
        {!hideCreateBoard && (
          <MenuItem
            variant={variant}
            icon={<FiPlus className="h-4 w-4" />}
            onSelect={(e) => {
              e.preventDefault();
              onCreateBoardClick();
            }}
            className={cn(
              'font-semibold menu-item-0',
              isBoardVariant && 'bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/20 focus:bg-gray-100 dark:focus:bg-white/20'
            )}
          >
            Create Board
          </MenuItem>
        )}
        <MenuItem
          variant={variant}
          icon={<FiUser className="h-4 w-4" />}
          onClick={onMyProfileClick}
          className={!hideCreateBoard ? 'menu-item-1' : 'menu-item-0'}
        >
          My Profile
        </MenuItem>
        <MenuItem
          variant={variant}
          icon={<FiGrid className="h-4 w-4" />}
          onClick={onBrowseBoardsClick}
          className={!hideCreateBoard ? 'menu-item-2' : 'menu-item-1'}
        >
          Browse Boards
        </MenuItem>
        <DropdownMenuSeparator
          className={cn(isBoardVariant ? 'bg-gray-200 dark:bg-white/10' : 'bg-border')}
        />
        <MenuItem
          variant={variant}
          icon={isDark ? <FiSun className="h-4 w-4" /> : <FiMoon className="h-4 w-4" />}
          onClick={toggleTheme}
          className={!hideCreateBoard ? 'menu-item-3' : 'menu-item-2'}
        >
          {isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        </MenuItem>
        <MenuItem
          variant={variant}
          icon={<FiLogOut className="h-4 w-4" />}
          onClick={onSignOut}
          className={!hideCreateBoard ? 'menu-item-4' : 'menu-item-3'}
        >
          Sign Out
        </MenuItem>
      </DropdownMenuContent>
      <CreateBoardDialog
        open={createBoardDialogOpen}
        onOpenChange={onCreateBoardDialogOpenChange}
      />
    </DropdownMenu>
  );
};

